import { z } from 'zod'
import {
  ValueObject,
  ResultOrError,
} from '@lukso/controllerapps-sharedlogic-ddd-core'

import { Profile } from '../../Profile'
import { Account } from '../../../wallet/Account'
import { CrossChainDeploymentPackage } from '../../../cross-chain-deployment/CrossChainDeploymentPackage'
import * as ProfileSchema from './ProfileSchema'
import * as CrossChainDeploymentPackageSchema from './CrossChainDeploymentPackageSchema'
import * as AccountSchema from './AccountSchema'
import { BackupReportParseError } from '../../../backup/errors/BackupReportParseError'

/**
 * Report schema
 */

const reportVersion = 2

export const BackupReportV2Schema = z.object({
  reportVersion: z.literal(reportVersion),
  profiles: z.array(ProfileSchema.ProfileSchema),
  crossChainDeploymentPackages: z.array(
    CrossChainDeploymentPackageSchema.CrossChainDeploymentPackageSchema
  ),
  accounts: z.array(AccountSchema.AccountSchema),
})

/**
 * Props schema
 */

export const BackupReportV2PropsSchema = z.object({
  profiles: z.array(z.instanceof(Profile)),
  crossChainDeploymentPackages: z.array(
    z.instanceof(CrossChainDeploymentPackage)
  ),
  accounts: z.array(z.instanceof(Account)),
})

export type BackupReportV2Props = z.infer<typeof BackupReportV2PropsSchema>

export class BackupReportV2 extends ValueObject<BackupReportV2Props> {
  public getProfiles(): Profile[] {
    return this.props.profiles
  }

  public getCrossChainDeploymentPackages(): CrossChainDeploymentPackage[] {
    return this.props.crossChainDeploymentPackages
  }

  public getAccounts(): Account[] {
    return this.props.accounts
  }

  public getReportText(spacing?: number): string {
    const { profiles, crossChainDeploymentPackages, accounts } = this.props
    return JSON.stringify(
      {
        reportVersion,
        profiles: profiles.map(p => ProfileSchema.mapDomainToReportText(p)),
        crossChainDeploymentPackages: crossChainDeploymentPackages.map(p =>
          CrossChainDeploymentPackageSchema.mapDomainToReportText(p)
        ),
        accounts: accounts.map(a => AccountSchema.mapDomainToReportText(a)),
      },
      undefined,
      spacing
    )
  }

  public static createFromReportText(
    reportText: string
  ): ResultOrError<BackupReportV2, BackupReportParseError> {
    let reportTextObject: string
    try {
      reportTextObject = JSON.parse(reportText)
    } catch (error) {
      return ResultOrError.error(
        new BackupReportParseError(`Invalid JSON in BackupReportV2: ${error}`)
      )
    }
    const validatedReport = BackupReportV2Schema.parse(reportTextObject)
    if (validatedReport.reportVersion !== reportVersion) {
      return ResultOrError.error(
        new BackupReportParseError('Invalid report version')
      )
    }
    const { profiles, crossChainDeploymentPackages, accounts } = validatedReport

    const report = BackupReportV2.create({
      profiles: profiles.map(p => ProfileSchema.mapReportTextToDomain(p)),
      crossChainDeploymentPackages: crossChainDeploymentPackages.map(p =>
        CrossChainDeploymentPackageSchema.mapReportTextToDomain(p)
      ),
      accounts: accounts.map(a => AccountSchema.mapReportTextToDomain(a)),
    })
    return ResultOrError.success(report)
  }

  public static create(props: BackupReportV2Props): BackupReportV2 {
    const validatedProps = BackupReportV2PropsSchema.parse(props)
    return new BackupReportV2(validatedProps)
  }
}
